/**
 * Shipping option definitions per market — the market-driven-shipping replacement for reading rates
 * off legacy delivery profiles / shipping zones.
 *
 * Deliberately kept separate from `GET_MARKETS_QUERY`: `Market.delivery` only exists from the
 * 2026-07 Admin schema on, newer than the version the app pins, so this operation runs with an API
 * version override (`MARKET_SHIPPING_OPTIONS_API_VERSION`). Folding it into the shared markets query
 * would put every consumer of that query on that version and, if the field ever moved again, would
 * break country-to-market mapping instead of just the rate selector.
 *
 * `name` isn't on the `DeliveryOptionDefinition` interface, only on its three concrete types, hence
 * the three inline fragments.
 */
export const GET_MARKET_SHIPPING_OPTIONS_QUERY = /* GraphQL */ `
    query GetMarketShippingOptions($first: Int!, $query: String) {
        markets(first: $first, query: $query) {
            nodes {
                id
                conditions {
                    regionsCondition {
                        regions(first: 50) {
                            nodes {
                                id
                                ... on MarketRegionCountry {
                                    code
                                }
                            }
                        }
                    }
                }
                delivery {
                    shipping {
                        optionDefinitions(first: 100) {
                            nodes {
                                id
                                description
                                ... on DeliveryFlatRateOptionDefinition {
                                    name
                                }
                                ... on DeliveryWeightBasedOptionDefinition {
                                    name
                                }
                                ... on DeliveryValueBasedOptionDefinition {
                                    name
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;

export const MARKET_SHIPPING_OPTIONS_API_VERSION = '2026-07';

export interface ShopifyDeliveryOptionDefinition {
    id: string;
    description?: string | null;
    name?: string | null;
}

export interface ShopifyMarketShippingOptions {
    id: string;
    conditions: {
        regionsCondition: {
            regions: { nodes: { id: string; code?: string }[] };
        } | null;
    } | null;
    delivery: {
        shipping: {
            optionDefinitions: { nodes: ShopifyDeliveryOptionDefinition[] };
        } | null;
    } | null;
}

export interface GetMarketShippingOptionsResponse {
    markets: {
        nodes: ShopifyMarketShippingOptions[];
    };
}

export interface GetMarketShippingOptionsVariables {
    [key: string]: unknown;
    first: number;
    query?: string;
}

/**
 * Extracts the numeric id from a Shopify global id, e.g.
 * `gid://shopify/DeliveryFlatRateOptionDefinition/123` -> `123`.
 *
 * Returns `undefined` when the tail isn't a plain number, so a rate that can't be expressed as the
 * mapping endpoint's `uint64` id is skipped rather than sent as `NaN`.
 */
export function parseShopifyGidId(gid: string | undefined | null): number | undefined {
    const tail = gid?.split('/').slice(-1)[0];

    return tail && /^\d+$/.test(tail) ? Number(tail) : undefined;
}

/**
 * Indexes each market's shipping option definitions by the ISO country codes it covers, so the rates
 * a country can be mapped to can be looked up by that country's code.
 */
export function groupOptionDefinitionsByCountryCode(
    markets: ShopifyMarketShippingOptions[]
): Record<string, ShopifyDeliveryOptionDefinition[]> {
    return markets.reduce<Record<string, ShopifyDeliveryOptionDefinition[]>>((acc, market) => {
        const definitions = market.delivery?.shipping?.optionDefinitions?.nodes ?? [];

        market.conditions?.regionsCondition?.regions?.nodes?.forEach((region) => {
            if (!region.code) return;

            const code = region.code.toUpperCase();
            const existing = acc[code] ?? (acc[code] = []);
            const seen = new Set(existing.map((definition) => definition.id));

            definitions.forEach((definition) => {
                if (!definition?.id || seen.has(definition.id)) return;

                seen.add(definition.id);
                existing.push(definition);
            });
        });

        return acc;
    }, {});
}
